import { HTTP_STATUS_FORBIDDEN, handleLogout } from "@auth/login"
import type { ErrorComponentProps } from "@tanstack/react-router"
import { Button } from "@ui"
import { NoAccessResponseError, ResponseError } from "@utils/request"
import type { ReactElement } from "react"

import { BeaverIllustration } from "../components/BeaverIllustration"
import { FoxIllustration } from "../components/FoxIllustration"
import { IllustratedErrorState } from "../components/IllustratedErrorState"

const HTTP_STATUS_UNAUTHORIZED = 401
const UNKNOWN_DETAIL = "Unknown Error"

const isRefusedSession = (cause: unknown): cause is ResponseError =>
  cause instanceof ResponseError &&
  (cause.statusCode === HTTP_STATUS_UNAUTHORIZED ||
    cause.statusCode === HTTP_STATUS_FORBIDDEN)

const signOutButton = (
  <Button
    variant="secondary"
    onClick={() => {
      void handleLogout()
    }}
  >
    Sign out
  </Button>
)

export const ErrorState = ({ error }: ErrorComponentProps): ReactElement => {
  const { cause } = error

  if (cause instanceof NoAccessResponseError) {
    return (
      <IllustratedErrorState
        title="The fox can't find your access"
        description={`You are missing access to ${cause.missing.length > 0 ? cause.missing.join(", ") : "something"}`}
        action={signOutButton}
      >
        <FoxIllustration />
      </IllustratedErrorState>
    )
  }

  if (isRefusedSession(cause)) {
    return (
      <IllustratedErrorState
        title="The beaver dammed this door"
        description={
          cause.detail && cause.detail !== UNKNOWN_DETAIL
            ? cause.detail
            : "Your account has no access to a Dovetail solution yet."
        }
        action={signOutButton}
      >
        <BeaverIllustration />
      </IllustratedErrorState>
    )
  }

  return (
    <IllustratedErrorState
      title="The beaver broke its hammer"
      description="Something gave way while loading Dovetail, so the build stopped here."
      error={error}
    >
      <BeaverIllustration />
    </IllustratedErrorState>
  )
}
